import { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AddActionDialogType, AddColumnDialogType, ColumnForm } from '@pages/EntityCreateOrEdit'

import {
  useFetchDictionariesByCategoryCodeQuery,
  useFetchObjectByCodeNoCacheMutation,
} from '@redux/api'

import { BIND_TYPE } from '@constants'
import { CATEGORIES, TIMELINE_ACTION_COLUMNS } from '@constants'
import {
  ActionRow,
  AutocompleteOption,
  ConfiguredColumn,
  Dictionary,
  GETShareColumn,
  ModalType,
  TimeLineGridConfig,
} from '@types'

type UseObjectYHandlersType = {
  methods: UseFormReturn<TimeLineGridConfig>
  handleSaveTimeLine: () => void
  bindingValuesColumns?: Dictionary[]
}

export const useObjectYHandlers = (props: UseObjectYHandlersType) => {
  const {
    methods: { setValue, watch },
    handleSaveTimeLine,
    bindingValuesColumns,
  } = props
  const { t } = useTranslation()

  const [fetchObjectByCode, { data }] = useFetchObjectByCodeNoCacheMutation({})
  const { data: bindingValuesAction } = useFetchDictionariesByCategoryCodeQuery(
    CATEGORIES.BINDING_TIME_TABLE_OBJECT_Y_ACTION
  )

  const watchObjectYObject = watch('objectY.object')
  const watchObjectYPreFilter = watch('objectY.preFilter')
  const watchObjectYCommandCode = watch('objectY.getDataCommand.name')
  const watchObjectYColumns = watch('objectY.columns')
  const watchObjectViewId = watch('objectY.entityId')
  const watchObjectYActions = watch('objectY.actions')

  const [objectYCurrentTab, setObjectYCurrentTab] = useState(0)
  const [showAddColumnDialog, setShowAddColumnDialog] = useState(false)
  const [addColumnDialogData, setAddColumnDialogData] = useState<
    Omit<AddColumnDialogType, 'onCreateColumn' | 'onUpdateColumn' | 'entityType'> | undefined
  >(undefined)
  const [showAddActionDialog, setShowAddActionDialog] = useState(false)
  const [addActionDialogData, setAddActionDialogData] =
    useState<
      Omit<
        AddActionDialogType,
        'createAction' | 'editAction' | 'showDialog' | 'onCloseModal' | 'actionEventsCategoryCode'
      >
    >()

  const selectedObjectFields = useMemo(() => {
    if (!watchObjectYObject || !data?.fields?.length) {
      return []
    }

    return data.fields
  }, [data])

  const selectedObjectFieldsOptions: AutocompleteOption[] = useMemo(() => {
    if (!watchObjectYObject || !data?.fields?.length) {
      return []
    }

    return data.fields.map(field => ({ id: field.name, label: field.name })) || []
  }, [watchObjectYObject, data])

  const selectedObjectCommandsOptions: AutocompleteOption[] = useMemo(() => {
    if (!watchObjectYObject || !data?.commands?.length) {
      return []
    }

    return (
      data.commands.map(field => ({
        id: field.code,
        label: field.code,
        httpMethod: field.httpMethod,
      })) || []
    )
  }, [watchObjectYObject, data])

  const objectYBindingValuesColumns = useMemo(() => {
    if (!bindingValuesColumns?.length) {
      return []
    }

    return bindingValuesColumns.filter(item => item.code !== BIND_TYPE.ACTION)
  }, [bindingValuesColumns])

  useEffect(() => {
    if (watchObjectYObject?.id) {
      fetchObjectByCode(watchObjectYObject.id as string)
    }
  }, [watchObjectYObject?.id])

  const handleChangeObjectYObject = (_: AutocompleteOption) => {
    setValue('objectY.keyField', null)
    setValue('objectY.getDataCommand.name', null)
    setValue('objectY.getDataCommand.parameters', [])
    setValue('objectY.getDataCommand.body', [])
  }

  const handleChangeObjectYPreFilter = (value: string) => {
    setValue('objectY.preFilter', value, { shouldDirty: true })
  }

  const handleChangeObjectYCommand = (option: AutocompleteOption) => {
    setValue('objectY.getDataCommand.name', option as AutocompleteOption<string>, {
      shouldValidate: true,
    })
    setValue('objectY.getDataCommand.parameters' as const, [])
    setValue('objectY.getDataCommand.body', [])
  }

  const handleChangeObjectYCurrentTab = (_?: SyntheticEvent, value: number) => {
    setObjectYCurrentTab(value)
  }

  const handleChangeObjectYColumns = (data: ColumnForm) => {
    const columnIndex = watchObjectYColumns?.findIndex(item => item.id === data.id)

    if (columnIndex < 0) {
      setValue('objectY.columns', watchObjectYColumns.concat(data))

      return
    }

    watchObjectYColumns[columnIndex] = data

    setValue('objectY.columns', watchObjectYColumns)
  }

  const handleDeleteObjectYColumn = (id: string | number) => {
    setValue(
      'objectY.columns',
      watchObjectYColumns.filter(item => item.id !== id) as ConfiguredColumn[]
    )

    handleSaveTimeLine()
  }

  const handleCloseObjectYAddColumnDialog = (isDirty?: boolean) => {
    if (!isDirty) {
      setShowAddColumnDialog(false)
      setAddColumnDialogData(undefined)

      return
    }

    if (confirm(t('notifications.leave'))) {
      setShowAddColumnDialog(false)
      setAddColumnDialogData(undefined)
    }
  }

  const handleOpenObjectYAddColumnDialog = (type: ModalType, id?: string | number) => {
    const columnData = watchObjectYColumns?.find(item => item.id === id)

    setShowAddColumnDialog(true)
    setAddColumnDialogData({
      columns: watchObjectYColumns,
      currentRow: columnData as ConfiguredColumn,
      modalType: type,
      objectCode: watchObjectYObject?.id as string,
      bindingValuesColumns: objectYBindingValuesColumns,
      rawObjectFields: selectedObjectFields,
      actionTable: {
        rows: [],
        columns: [],
      },
      entityCode: '',
      entityFormCode: '',
      showDialog: showAddColumnDialog,
      onCloseModal: handleCloseObjectYAddColumnDialog,
    })
  }

  const handleCloseObjectYAddActionDialog = (isDirty?: boolean) => {
    if (!isDirty) {
      setShowAddActionDialog(false)
      setAddActionDialogData(undefined)

      return
    }

    if (confirm(t('notifications.leave'))) {
      setShowAddActionDialog(false)
      setAddActionDialogData(undefined)
    }
  }

  const handleOpenObjectYAddActionDialog = (modalType: ModalType, id?: string | number) => {
    const actionData = watchObjectYActions?.find(item => item.id === id)

    setShowAddActionDialog(true)
    setAddActionDialogData({
      modalType,
      bindingValuesAction,
      objectCode: watchObjectYObject?.id as string,
      currentRow: actionData as GETShareColumn,
      actionTable: {
        columns: TIMELINE_ACTION_COLUMNS,
        rows: watchObjectYActions,
      },
      columns: [],
    })
  }

  const handleChangeObjectYAction = (data: ActionRow) => {
    const columnIndex = watchObjectYActions?.findIndex(item => item.id === data.id)

    if (columnIndex && columnIndex < 0) {
      setValue('objectY.actions', watchObjectYActions.concat(data))

      return
    }

    watchObjectYActions[columnIndex] = data
    setValue('objectY.actions', watchObjectYActions)
  }

  const handleDeleteObjectYAction = (id: string | number) => {
    setValue(
      'objectY.actions' as const,
      watchObjectYActions.filter(item => item.id !== id)
    )

    return handleSaveTimeLine()
  }

  return {
    state: {
      watchObjectYObject,
      watchObjectYPreFilter,
      watchObjectYCommandCode,
      watchObjectYColumns,
      watchObjectViewId,
      watchObjectYActions,
      objectYCurrentTab,
      objectYShowAddColumnDialog: showAddColumnDialog,
      objectYAddColumnDialogData: addColumnDialogData,
      objectYAddActionDialogData: addActionDialogData,
      objectYShowAddActionDialog: showAddActionDialog,
    },
    data: {
      objectYSelectedObjectCommandsOptions: selectedObjectCommandsOptions,
      objectYSelectedObjectFieldsOptions: selectedObjectFieldsOptions,
      objectYSelectedObjectFields: selectedObjectFields,
    },
    handlers: {
      handleChangeObjectYObject,
      handleChangeObjectYPreFilter,
      handleChangeObjectYCommand,
      handleChangeObjectYCurrentTab,
      handleChangeObjectYColumns,
      handleDeleteObjectYColumn,
      handleCloseObjectYAddColumnDialog,
      handleOpenObjectYAddColumnDialog,
      handleOpenObjectYAddActionDialog,
      handleDeleteObjectYAction,
      handleChangeObjectYAction,
      handleCloseObjectYAddActionDialog,
    },
  }
}
